import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Save, Loader2, Plus, Trash2, GripVertical,
  ChevronUp, ChevronDown, Eye, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { settingsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const TermsEditorPage = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const canEdit = hasPermission('settings.edit');

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const res = await settingsApi.getTerms();
      setSections(res.data.sections || []);
      setLastUpdated(res.data.last_updated || '');
      // Expand all sections by default
      const expanded = {};
      res.data.sections?.forEach((s, i) => expanded[i] = true);
      setExpandedSections(expanded);
    } catch (error) {
      console.error('Error fetching terms:', error);
      toast.error('Error al cargar términos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error('No tenés permisos para editar');
      return;
    }

    setSaving(true);
    try {
      await settingsApi.updateTerms({ sections });
      toast.success('Términos actualizados correctamente');
      fetchTerms(); // Refresh to get new last_updated
    } catch (error) {
      console.error('Error saving terms:', error);
      toast.error('Error al guardar términos');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: 'Nueva Sección',
      content: 'Contenido de la sección...'
    };
    setSections([...sections, newSection]);
    setExpandedSections({ ...expandedSections, [sections.length]: true });
  };

  const removeSection = (index) => {
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
  };

  const updateSection = (index, field, value) => {
    const newSections = [...sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setSections(newSections);
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSections(newSections);
  };

  const toggleExpand = (index) => {
    setExpandedSections({
      ...expandedSections,
      [index]: !expandedSections[index]
    });
  };

  // Render markdown-like content as HTML for preview
  const renderContent = (content) => {
    if (!content) return '';
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/^- /gm, '• ')
      .replace(/\n/g, '<br/>');
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="terms-editor-page" className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link 
                to="/admin/formularios" 
                className="inline-flex items-center text-[#A1A1AA] hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft size={16} className="mr-2" />
                Volver a Formularios
              </Link>
              <h1 className="text-3xl font-black uppercase tracking-tighter flex items-center gap-3">
                <FileText className="text-[#D4AF37]" />
                Términos y Condiciones
              </h1>
              <p className="text-[#A1A1AA] mt-1">
                Última actualización: {lastUpdated || 'Nunca'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setPreviewOpen(true)}
                variant="outline"
                className="border-[#27272A] hover:bg-[#1A1A1A]"
              >
                <Eye size={16} className="mr-2" />
                Vista Previa
              </Button>
              {canEdit && (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-terms-btn"
                >
                  {saving ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Save size={16} className="mr-2" />
                  )}
                  Guardar Cambios
                </Button>
              )}
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-[#D4AF37]">
              <strong>Formato:</strong> Usá **texto** para negrita y - al inicio de línea para viñetas.
              Los cambios se reflejarán en la página pública /terminos.
            </p>
          </div>

          {/* Sections Editor */}
          <div className="space-y-4">
            {sections.map((section, index) => (
              <motion.div
                key={section.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden"
              >
                {/* Section Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1A1A1A] transition-colors"
                  onClick={() => toggleExpand(index)}
                >
                  <div className="flex items-center gap-3">
                    <GripVertical size={20} className="text-[#52525B]" />
                    <span className="text-[#D4AF37] font-bold">{index + 1}.</span>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => updateSection(index, 'title', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-transparent border-none text-lg font-bold text-white focus:outline-none focus:ring-1 focus:ring-[#D4AF37] px-2 py-1 rounded"
                      placeholder="Título de la sección"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); moveSection(index, 'up'); }}
                      disabled={index === 0 || !canEdit}
                      className="p-1 text-[#52525B] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronUp size={18} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveSection(index, 'down'); }}
                      disabled={index === sections.length - 1 || !canEdit}
                      className="p-1 text-[#52525B] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronDown size={18} />
                    </button>
                    {canEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeSection(index); }}
                        className="p-1 text-red-500 hover:text-red-400"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                    {expandedSections[index] ? (
                      <ChevronUp size={20} className="text-[#A1A1AA]" />
                    ) : (
                      <ChevronDown size={20} className="text-[#A1A1AA]" />
                    )}
                  </div>
                </div>

                {/* Section Content */}
                {expandedSections[index] && (
                  <div className="p-4 pt-0 border-t border-[#27272A]">
                    <Label className="text-xs uppercase tracking-widest text-[#52525B] mb-2 block mt-4">
                      Contenido
                    </Label>
                    <textarea
                      value={section.content}
                      onChange={(e) => updateSection(index, 'content', e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 text-[#A1A1AA] focus:outline-none focus:border-[#D4AF37] min-h-[200px] font-mono text-sm resize-y"
                      placeholder="Contenido de la sección..."
                      disabled={!canEdit}
                    />
                    
                    {/* Preview of this section */}
                    <div className="mt-3 p-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg">
                      <p className="text-xs text-[#52525B] uppercase tracking-widest mb-2">Vista previa</p>
                      <div 
                        className="text-sm text-[#A1A1AA]"
                        dangerouslySetInnerHTML={{ __html: renderContent(section.content) }}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Add Section Button */}
          {canEdit && (
            <motion.button
              onClick={addSection}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full mt-6 p-4 border-2 border-dashed border-[#27272A] hover:border-[#D4AF37] rounded-lg text-[#52525B] hover:text-[#D4AF37] transition-colors flex items-center justify-center gap-2"
              data-testid="add-section-btn"
            >
              <Plus size={20} />
              Agregar Sección
            </motion.button>
          )}

          {/* Preview Dialog */}
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-[#09090B] border-[#27272A]">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                  <FileText className="text-[#D4AF37]" />
                  Vista Previa - Términos y Condiciones
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {sections.map((section, index) => (
                  <div 
                    key={section.id || index}
                    className="bg-[#121212] border border-[#27272A] rounded-xl p-6"
                  >
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <span className="text-[#D4AF37]">{index + 1}.</span> {section.title}
                    </h2>
                    <div 
                      className="text-[#A1A1AA] leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: renderContent(section.content) }}
                    />
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default TermsEditorPage;
